import { type FC, useCallback, useMemo, useState } from 'react';
import type { AttachmentsByExternalId } from '../model/attachments';
import {
  type BrokenContentImage,
  extractForwardedBody,
  findForwardedBlock,
  getForwardedMeta,
} from '../model/htmlContent';
import { AttachmentLightbox, type LightboxImage } from './AttachmentLightbox';
import { BrokenImagesDropdown } from './BrokenImagesDropdown';
import { renderContentNodes } from './contentNodes';
import { ForwardedMessage } from './ForwardedMessage';
import { LargeAttachmentsGroup } from './LargeAttachmentsGroup';

type Props = {
  /** Оформленное содержимое — очищенное и разобранное ({@link parseContentHtml}) */
  root: HTMLElement;
  /** Вложения сообщения по внешнему идентификатору */
  attachmentsByExternalId: AttachmentsByExternalId;
};

/**
 * Оформленное содержимое сообщения: показывается оформленным, разметка не видна, ничего из него
 * не исполняется. Пересланное письмо свёрнуто, картинки открываются увеличенными, а не
 * загрузившиеся уходят из текста в конец облака — мелкие кнопкой «···», крупные группой вложений.
 */
export const HtmlContent: FC<Props> = ({ root, attachmentsByExternalId }) => {
  const [brokenImages, setBrokenImages] = useState<BrokenContentImage[]>([]);
  const [lightboxImage, setLightboxImage] = useState<LightboxImage | null>(null);

  const forwarded = useMemo(() => {
    const block = findForwardedBlock(root);
    if (!block) return null;
    return { block, meta: getForwardedMeta(block), body: extractForwardedBody(block) };
  }, [root]);

  const handleImageError = useCallback((image: BrokenContentImage) => {
    setBrokenImages((current) => (current.some(({ key }) => key === image.key) ? current : [...current, image]));
  }, []);

  const nodes = useMemo(
    () =>
      renderContentNodes(root, {
        attachmentsByExternalId,
        brokenImageKeys: new Set(brokenImages.map(({ key }) => key)),
        forwarded: forwarded && {
          block: forwarded.block,
          node: (
            <ForwardedMessage meta={forwarded.meta}>
              <HtmlContent root={forwarded.body} attachmentsByExternalId={attachmentsByExternalId} />
            </ForwardedMessage>
          ),
        },
        onImageOpen: setLightboxImage,
        onImageError: handleImageError,
      }),
    [root, attachmentsByExternalId, brokenImages, forwarded, handleImageError],
  );

  const smallBrokenImages = brokenImages.filter(({ isSmall }) => isSmall);
  const largeBrokenImages = brokenImages.filter(({ isSmall }) => !isSmall);

  return (
    <>
      {/* Вёрстка письма с заданной шириной (таблицы, блоки) шире облака — прокручивается внутри него */}
      <div className="min-w-0 overflow-x-auto wrap-break-word [&_a]:text-primary [&_a]:underline [&_li]:ml-4 [&_ol]:list-decimal [&_ul]:list-disc">
        {nodes}
      </div>
      {brokenImages.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {largeBrokenImages.length > 0 && <LargeAttachmentsGroup images={largeBrokenImages} />}
          {smallBrokenImages.length > 0 && <BrokenImagesDropdown images={smallBrokenImages} />}
        </div>
      )}
      <AttachmentLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
    </>
  );
};
